import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Building,
  ClipboardList,
  Sparkles,
  FileText,
  FileCheck2,
  CheckCircle2,
  Sliders,
  PenTool,
  CheckSquare,
  Scale,
  ShieldAlert,
  Trophy,
  Archive,
  Trash2,
  Lock,
  Send,
} from "lucide-react";
import { Opportunity, OpportunityStage, UserProfile } from "../../types";
import {
  subscribeToOpportunity,
  saveQualifyChecklist,
  transitionOpportunityStage,
  archiveOpportunity,
  deleteOpportunity,
} from "../../services/opportunitiesService";
import {
  setActiveOpportunity,
  clearActiveOpportunity,
} from "../../services/activeOpportunityService";
import { IntakeTab } from "../workspace/IntakeTab";
import { PlanTab } from "../workspace/PlanTab";
import { DraftingTab } from "../workspace/DraftingTab";
import { CoachTab } from "../workspace/CoachTab";
import { AssessmentTab } from "../workspace/AssessmentTab";
import { RedTeamTab } from "../workspace/RedTeamTab";
import { SubmitTab } from "../workspace/SubmitTab";
import { DebriefTab } from "../workspace/DebriefTab";
import { QualifyModal } from "../pipeline/QualifyModal";
import { PostMortemModal } from "../pipeline/PostMortemModal";
import { ArchiveOpportunityModal } from "../pipeline/ArchiveOpportunityModal";
import { DeleteOpportunityModal } from "../pipeline/DeleteOpportunityModal";
import { HarvestLibraryModal } from "../workspace/HarvestLibraryModal";
import { ExpandableCard } from "../common/ExpandableCard";

interface OpportunityWorkspaceViewProps {
  user: UserProfile | null;
}

type WorkspaceTab =
  | "intake"
  | "assessment"
  | "requirements"
  | "drafting"
  | "coach"
  | "redteam"
  | "submit"
  | "debrief";

export const OpportunityWorkspaceView: React.FC<OpportunityWorkspaceViewProps> = ({ user }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<WorkspaceTab>("intake");
  const [isQualifyOpen, setIsQualifyOpen] = useState(false);
  const [isPostMortemOpen, setIsPostMortemOpen] = useState(false);
  const [isHarvestOpen, setIsHarvestOpen] = useState(false);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [stagedCoachEdit, setStagedCoachEdit] = useState<{
    requirementId: string;
    text: string;
  } | null>(null);

  const currentUser = {
    displayName: user?.displayName || "User",
    email: user?.email || "mark@mlconnections.com",
  };

  const handleStageChange = async (newStage: OpportunityStage) => {
    if (!opportunity) return;
    await transitionOpportunityStage(
      opportunity.id,
      newStage,
      currentUser,
      `Stage transitioned to ${newStage.replace("_", " ")} from workspace`,
      opportunity.stageHistory || [],
    );
  };

  const handleArchive = async (reason: string, notes?: string) => {
    if (!opportunity) return;
    clearActiveOpportunity(opportunity.id);
    await archiveOpportunity(
      opportunity.id,
      reason,
      currentUser,
      opportunity.stageHistory || [],
      notes,
    );
  };

  const handleDelete = async (oppId: string) => {
    clearActiveOpportunity(oppId);
    await deleteOpportunity(oppId);
    navigate("/");
  };

  useEffect(() => {
    if (!id) return;
    const unsub = subscribeToOpportunity(
      id,
      (opp) => {
        setOpportunity(opp);
        setLoading(false);
        if (opp) {
          setActiveOpportunity({
            id: opp.id,
            title: opp.title,
            client: opp.client,
            stage: opp.stage,
          });
        }
      },
      (err) => {
        console.error("Error loading opportunity:", err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [id]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center space-y-2">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2ba5b5] border-r-transparent" />
          <p className="text-xs text-slate-500">Loading opportunity workspace...</p>
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 p-12 text-center dark:border-slate-800">
        <Building className="mx-auto h-12 w-12 text-slate-300 dark:text-slate-600" />
        <h3 className="mt-3 text-base font-semibold text-slate-900 dark:text-white">
          Opportunity Not Found
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          This RFP record may have been deleted or moved.
        </p>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="btn-animate mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white hover:bg-[#238a97]"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Pipeline</span>
        </button>
      </div>
    );
  }

  const summary = opportunity.requirementsSummary;
  const totalReqs = summary?.total || 0;
  const confirmedReqs = summary?.confirmed || 0;
  const formsTotal = summary?.formsTotal || 0;
  const rulesTotal = summary?.formatRulesTotal || 0;

  return (
    <div className="space-y-6">
      {/* Header & Breadcrumb */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="btn-animate inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>Back to Pipeline</span>
        </button>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#2ba5b5]">
                {opportunity.client}
              </span>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-slate-400 font-semibold uppercase">Stage:</span>
                <select
                  value={opportunity.stage}
                  onChange={(e) => handleStageChange(e.target.value as OpportunityStage)}
                  className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs font-bold uppercase text-slate-700 shadow-2xs outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  <option value="reviewing">Intake & Reviewing</option>
                  <option value="go">Qualified (Go)</option>
                  <option value="no_go">Declined (No-Go)</option>
                  <option value="drafting">Drafting</option>
                  <option value="in_review">In Review</option>
                  <option value="approved">Approved</option>
                  <option value="submitted">Submitted</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                  <option value="archived">Archived / Ended Early</option>
                </select>
              </div>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              {opportunity.title}
            </h1>
          </div>

          {/* Quick Info & Action Bar */}
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="flex items-center gap-1.5 rounded-lg border border-slate-200/80 bg-white px-3 py-1.5 text-xs text-slate-600 shadow-2xs dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
              <Calendar className="h-3.5 w-3.5 text-[#2ba5b5]" />
              <span>Due: {opportunity.dueDate}</span>
            </div>

            {opportunity.assessmentSummary && (
              <button
                type="button"
                onClick={() => setActiveTab("assessment")}
                className={`btn-animate inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                  opportunity.assessmentSummary.recommendation === "go"
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                    : opportunity.assessmentSummary.recommendation === "caution"
                      ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                      : "border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300"
                }`}
              >
                <Scale className="h-3.5 w-3.5" />
                <span>
                  {opportunity.assessmentSummary.overallFitScore}% Fit (
                  {opportunity.assessmentSummary.recommendation.toUpperCase()})
                </span>
              </button>
            )}

            {opportunity.redTeamSummary && (
              <button
                type="button"
                onClick={() => setActiveTab("redteam")}
                className={`btn-animate inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                  opportunity.redTeamSummary.overallGrade === "A" ||
                  opportunity.redTeamSummary.overallGrade === "B"
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                    : opportunity.redTeamSummary.overallGrade === "C"
                      ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                      : "border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300"
                }`}
              >
                <ShieldAlert className="h-3.5 w-3.5" />
                <span>
                  Grade {opportunity.redTeamSummary.overallGrade} (
                  {opportunity.redTeamSummary.overallWeightedScore}%)
                </span>
              </button>
            )}

            {opportunity.outcome ? (
              <div className="flex items-center gap-1.5">
                <span
                  className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-semibold uppercase ${
                    opportunity.outcome === "won"
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                      : "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                  }`}
                >
                  {opportunity.outcome === "won" && (
                    <Trophy className="h-3.5 w-3.5 text-amber-500" />
                  )}
                  <span>Outcome: {opportunity.outcome}</span>
                </span>
                <button
                  type="button"
                  onClick={() => setIsPostMortemOpen(true)}
                  className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-300"
                >
                  <FileText className="h-3.5 w-3.5" />
                  <span>Debrief</span>
                </button>
              </div>
            ) : opportunity.stage === "submitted" ? (
              <button
                type="button"
                onClick={() => setIsPostMortemOpen(true)}
                className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-300"
              >
                <Trophy className="h-3.5 w-3.5" />
                <span>Record Outcome & Debrief</span>
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => setIsHarvestOpen(true)}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
              title="Harvest new reusable responses into the Answer Library"
            >
              <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
              <span>Harvest to Library</span>
            </button>

            <button
              type="button"
              onClick={() => setIsQualifyOpen(true)}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3.5 py-1.5 text-xs font-semibold text-[#2ba5b5] hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300"
            >
              <ClipboardList className="h-4 w-4" />
              <span>
                {opportunity.qualifyChecklist
                  ? "Qualify Checklist (Complete)"
                  : "Qualify Checklist"}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setIsArchiveOpen(true)}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300"
              title="Archive or end opportunity early"
            >
              <Archive className="h-3.5 w-3.5 text-amber-600" />
              <span>Archive</span>
            </button>

            <button
              type="button"
              onClick={() => setIsDeleteOpen(true)}
              className="btn-animate rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:border-slate-700 dark:hover:bg-rose-950/40"
              title="Delete opportunity permanently"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Readiness Metrics Strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200/80 bg-white p-3.5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Requirements</span>
            <FileText className="h-4 w-4 text-[#2ba5b5]" />
          </div>
          <div className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            {confirmedReqs}{" "}
            <span className="text-xs font-normal text-slate-400">/ {totalReqs} confirmed</span>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-white p-3.5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Required Forms</span>
            <FileCheck2 className="h-4 w-4 text-[#2ba5b5]" />
          </div>
          <div className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            {formsTotal} <span className="text-xs font-normal text-slate-400">forms tracked</span>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-white p-3.5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Format Rules</span>
            <Sliders className="h-4 w-4 text-[#2ba5b5]" />
          </div>
          <div className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            {rulesTotal} <span className="text-xs font-normal text-slate-400">rules</span>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-white p-3.5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Decision</span>
            <CheckCircle2 className="h-4 w-4 text-[#2ba5b5]" />
          </div>
          <div className="mt-1 text-lg font-bold text-slate-900 dark:text-white uppercase">
            {opportunity.decision || "Undecided"}
          </div>
        </div>
      </div>

      {/* Post-Mortem Debrief Card */}
      {opportunity.postMortem && (
        <ExpandableCard
          title={`Post-Mortem Debrief: ${opportunity.outcome?.toUpperCase() || "RECORDED"}`}
          badge={opportunity.outcome?.toUpperCase() || "RECORDED"}
          badgeColor={
            opportunity.outcome === "won"
              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
              : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300"
          }
          summary={`Recorded on ${opportunity.postMortem.recordedAt?.slice(0, 10) || "Recent"} by ${opportunity.postMortem.recordedByEmail}`}
          defaultExpanded={false}
          headerActions={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsPostMortemOpen(true);
                }}
                className="btn-animate rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                Edit Debrief
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsHarvestOpen(true);
                }}
                className="btn-animate rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
              >
                Harvest to Library
              </button>
            </div>
          }
        >
          <div className="space-y-3 text-xs text-slate-700 dark:text-slate-300">
            {opportunity.postMortem.factors && opportunity.postMortem.factors.length > 0 && (
              <div>
                <span className="font-semibold text-slate-900 dark:text-white">
                  Contributing Factors:{" "}
                </span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {opportunity.postMortem.factors.map((factor) => (
                    <span
                      key={factor}
                      className="rounded-md bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 uppercase"
                    >
                      {factor.replace("_", " ")}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {opportunity.postMortem.winningCompetitor && (
              <div>
                <span className="font-semibold text-slate-900 dark:text-white">
                  Winning Competitor:{" "}
                </span>
                <span>{opportunity.postMortem.winningCompetitor}</span>
              </div>
            )}
            {opportunity.postMortem.clientFeedbackNotes && (
              <div>
                <span className="font-semibold text-slate-900 dark:text-white">
                  Client Feedback:{" "}
                </span>
                <p className="mt-0.5 italic text-slate-600 dark:text-slate-400">
                  &ldquo;{opportunity.postMortem.clientFeedbackNotes}&rdquo;
                </p>
              </div>
            )}
            {opportunity.postMortem.lessonsLearned && (
              <div>
                <span className="font-semibold text-slate-900 dark:text-white">
                  Internal Lessons Learned:{" "}
                </span>
                <p className="mt-0.5 text-slate-600 dark:text-slate-400">
                  {opportunity.postMortem.lessonsLearned}
                </p>
              </div>
            )}
          </div>
        </ExpandableCard>
      )}

      {/* Main Tabs Navigation */}
      <div className="flex border-b border-slate-200 dark:border-slate-800">
        <button
          type="button"
          onClick={() => setActiveTab("intake")}
          className={`flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-semibold transition-colors ${
            activeTab === "intake"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
          }`}
        >
          <Sparkles className="h-4 w-4" />
          <span>Intake & RFP Extraction</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("assessment")}
          className={`flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-semibold transition-colors ${
            activeTab === "assessment"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
          }`}
        >
          <Scale className="h-4 w-4" />
          <span>Strategic Assessment & Fit</span>
          {opportunity.assessmentSummary && (
            <span
              className={`rounded-full px-1.5 py-0.2 text-[10px] font-bold ${
                opportunity.assessmentSummary.recommendation === "go"
                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                  : opportunity.assessmentSummary.recommendation === "caution"
                    ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                    : "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
              }`}
            >
              {opportunity.assessmentSummary.overallFitScore}%
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("requirements")}
          className={`flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-semibold transition-colors ${
            activeTab === "requirements"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
          }`}
        >
          <ClipboardList className="h-4 w-4" />
          <span>Response Plan</span>
          {opportunity.planFrozen ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-600/20 dark:bg-emerald-950/50 dark:text-emerald-300"
              title="Response Plan baseline frozen"
            >
              <Lock className="h-2.5 w-2.5" />
              <span>Frozen</span>
            </span>
          ) : totalReqs > 0 ? (
            <span className="rounded-full bg-slate-100 px-1.5 py-0.2 text-[10px] dark:bg-slate-800">
              {totalReqs}
            </span>
          ) : null}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("drafting")}
          className={`flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-semibold transition-colors ${
            activeTab === "drafting"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
          }`}
        >
          <PenTool className="h-4 w-4" />
          <span>Drafting Board</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("coach")}
          className={`flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-semibold transition-colors ${
            activeTab === "coach"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
          }`}
        >
          <CheckSquare className="h-4 w-4" />
          <span>Completeness & Coach</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("redteam")}
          className={`flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-semibold transition-colors ${
            activeTab === "redteam"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
          }`}
        >
          <ShieldAlert className="h-4 w-4" />
          <span>Evaluator Red Team</span>
          {opportunity.redTeamSummary && (
            <span
              className={`rounded-full px-1.5 py-0.2 text-[10px] font-bold ${
                opportunity.redTeamSummary.overallGrade === "A" ||
                opportunity.redTeamSummary.overallGrade === "B"
                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                  : opportunity.redTeamSummary.overallGrade === "C"
                    ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                    : "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
              }`}
            >
              Grade {opportunity.redTeamSummary.overallGrade}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("submit")}
          className={`flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-semibold transition-colors ${
            activeTab === "submit"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
          }`}
        >
          <Send className="h-4 w-4" />
          <span>Final Submission</span>
          {opportunity.stage === "submitted" ? (
            <span className="rounded-full bg-emerald-100 px-1.5 py-0.2 text-[10px] font-bold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
              Submitted
            </span>
          ) : opportunity.leadSignOff && opportunity.executiveSignOff ? (
            <span className="rounded-full bg-teal-100 px-1.5 py-0.2 text-[10px] font-bold text-teal-800 dark:bg-teal-950 dark:text-teal-300">
              Signed
            </span>
          ) : null}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("debrief")}
          className={`flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-semibold transition-colors ${
            activeTab === "debrief"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
          }`}
        >
          <Trophy className="h-4 w-4" />
          <span>Debrief & Learn</span>
          {opportunity.outcome === "won" ? (
            <span className="rounded-full bg-emerald-100 px-1.5 py-0.2 text-[10px] font-bold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
              Won 🏆
            </span>
          ) : opportunity.outcome === "lost" ? (
            <span className="rounded-full bg-rose-100 px-1.5 py-0.2 text-[10px] font-bold text-rose-800 dark:bg-rose-950 dark:text-rose-300">
              Lost
            </span>
          ) : opportunity.stage === "submitted" ? (
            <span className="rounded-full bg-amber-100 px-1.5 py-0.2 text-[10px] font-bold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
              Pending
            </span>
          ) : null}
        </button>
      </div>

      {/* Tab Panels */}
      {activeTab === "intake" && (
        <IntakeTab
          opportunity={opportunity}
          user={user}
          onSwitchTab={(t) => setActiveTab(t as WorkspaceTab)}
          onOpenQualifyModal={() => setIsQualifyOpen(true)}
        />
      )}

      {activeTab === "assessment" && (
        <AssessmentTab
          opportunity={opportunity}
          onOpenQualifyModal={() => setIsQualifyOpen(true)}
        />
      )}

      {activeTab === "requirements" && <PlanTab opportunity={opportunity} user={user} />}

      {activeTab === "drafting" && (
        <DraftingTab
          opportunity={opportunity}
          user={user}
          stagedEdit={stagedCoachEdit}
          onClearStagedEdit={() => setStagedCoachEdit(null)}
          onSwitchTab={(t) => setActiveTab(t as WorkspaceTab)}
          onOpenHarvest={() => setIsHarvestOpen(true)}
        />
      )}

      {activeTab === "coach" && (
        <CoachTab
          opportunity={opportunity}
          user={user}
          onApplySuggestion={(reqId, text) => {
            setStagedCoachEdit({ requirementId: reqId, text });
            setActiveTab("drafting");
          }}
          onSwitchTab={(t) => setActiveTab(t as WorkspaceTab)}
        />
      )}

      {activeTab === "redteam" && (
        <RedTeamTab
          opportunity={opportunity}
          user={user}
          onSwitchTab={(t) => setActiveTab(t as WorkspaceTab)}
        />
      )}

      {activeTab === "submit" && (
        <SubmitTab
          opportunity={opportunity}
          user={user}
          onSwitchTab={(t) => setActiveTab(t as WorkspaceTab)}
          onOpenDebrief={() => setActiveTab("debrief")}
        />
      )}

      {activeTab === "debrief" && (
        <DebriefTab
          opportunity={opportunity}
          user={user}
          onSwitchTab={(t) => setActiveTab(t as WorkspaceTab)}
          onOpenHarvest={() => setIsHarvestOpen(true)}
        />
      )}

      {/* Qualify Modal */}
      {isQualifyOpen && (
        <QualifyModal
          isOpen={isQualifyOpen}
          opportunity={opportunity}
          onClose={() => setIsQualifyOpen(false)}
          onSave={async (checklist) => {
            if (!user) return;
            await saveQualifyChecklist(
              opportunity.id,
              checklist,
              { displayName: user.displayName || "User", email: user.email },
              opportunity.stageHistory || [],
            );
            setIsQualifyOpen(false);
          }}
        />
      )}

      {/* Post-Mortem Debrief Modal */}
      {isPostMortemOpen && (
        <PostMortemModal
          isOpen={isPostMortemOpen}
          opportunity={opportunity}
          user={user}
          onClose={() => setIsPostMortemOpen(false)}
          onOpenHarvest={() => {
            setIsPostMortemOpen(false);
            setIsHarvestOpen(true);
          }}
        />
      )}

      {/* Harvest Candidates Modal */}
      {isHarvestOpen && (
        <HarvestLibraryModal
          isOpen={isHarvestOpen}
          opportunity={opportunity}
          user={user}
          onClose={() => setIsHarvestOpen(false)}
        />
      )}

      {/* Archive / End Early Modal */}
      {isArchiveOpen && (
        <ArchiveOpportunityModal
          isOpen={isArchiveOpen}
          opportunity={opportunity}
          onClose={() => setIsArchiveOpen(false)}
          onConfirmArchive={handleArchive}
        />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteOpen && (
        <DeleteOpportunityModal
          isOpen={isDeleteOpen}
          opportunity={opportunity}
          onClose={() => setIsDeleteOpen(false)}
          onConfirmDelete={handleDelete}
          onArchiveInstead={() => {
            setIsDeleteOpen(false);
            setIsArchiveOpen(true);
          }}
        />
      )}
    </div>
  );
};
